import { ColumnsType } from 'antd/es/table';
import { Dictionary } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Role, User, UserType } from 'api/models';
import { Avatar } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import { FilterDrawer } from 'components/molecules/filter-drawer';
import { defaultFilter } from 'components/molecules/filters/utils';
import { TaskFilterButton } from 'components/molecules/views/components';
import { useUserCustomFieldsAsFilterableFields } from 'components/organisms/module-settings/team-members/section-field/hooks';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { getFilterFunction } from 'components/organisms/tasks/get-filter-function';
import { useTranslation } from 'hooks/use-translation';
import { useActiveOrInvitedNonCustomerUsersQuery } from 'queries/members';
import { useVendorRolesQuery } from 'queries/roles';
import { fullName } from 'utils/full-name';
import { getThumbnailUrl } from 'utils/get-thumbnail-url';
import { sortByText } from 'utils/sort-by-text';
import { useUserFieldsAsFilterableFields } from 'utils/user-fields';
import { TextWrapperWithEllipsis } from '../../components';
import { RoleSelectionDropdown } from '../../role-selection';
import { AddToProjectRecordType, ProjectsWithMembers } from '../../types';
import { userAsFilterFormat } from '../../utils/resource-matching';
import { getUserRoleOptions } from '../../utils/role-options';
import {
  PartnerCompanyInfo,
  ModalContent,
  NameCellWithoutPadding,
} from './common';

interface Props {
  projectId: number;
  projectLookup: Dictionary<ProjectsWithMembers>;
  selectedUsers: User[];
  updateSelectedUsers: (users: User[]) => void;
}

export const AddUsersModalContent: FC<Props> = ({
  projectId,
  projectLookup,
  selectedUsers,
  updateSelectedUsers,
}) => {
  const { data: users } = useActiveOrInvitedNonCustomerUsersQuery();
  const { data: roles = [] } = useVendorRolesQuery();
  const { fields: userCustomFields } = useUserCustomFieldsAsFilterableFields();
  const { fields: allUserFields, isFieldsLoading: isUserFieldsLoading } =
    useUserFieldsAsFilterableFields();

  const projectUserIds = useMemo(
    () =>
      projectLookup[projectId]?.projectMembers
        .filter(
          ({ role, referenceTeam }) => !(role && referenceTeam), // this is to only count users present in native roles
        )
        .map(({ user: { userId } }) => userId) ?? [],
    [projectId, projectLookup],
  );

  const usersNotInProjects = useMemo(() => {
    const partnersInProject = (projectLookup[projectId]?.partners ?? []).map(
      ({ companyId }) => companyId,
    );

    return (users ?? []).filter(
      ({ userId, userType, company: { companyId } }) =>
        !projectUserIds.includes(userId) &&
        (userType === UserType.Partner
          ? partnersInProject.includes(companyId)
          : true),
    );
  }, [projectId, projectLookup, projectUserIds, users]);

  const searchPlaceholder = useTranslation(
    'resource_management.add_users.search.placeholder',
  );

  const [choosenRoles, setChoosenRoles] = useState<Role[]>([]);
  const choosenRoleIds = useMemo(
    () => choosenRoles.map(({ roleId }) => roleId),
    [choosenRoles],
  );
  const [filter, setFilter] = useState(defaultFilter);
  const validFilters = useMemo(
    () => filter.criteria.filter(FieldFilter.isValid),
    [filter.criteria],
  );
  const filterFunction = useMemo(() => {
    return getFilterFunction(filter) ?? (() => true);
  }, [filter]);

  const combinedFilterFunction = useCallback(
    (user: User, searchText: string) =>
      (choosenRoleIds.length === 0 ||
        Boolean(
          user.role?.roleId && choosenRoleIds.includes(user.role?.roleId),
        )) &&
      Boolean(
        searchText === '' ||
          fullName(user).toLowerCase().includes(searchText.toLowerCase()),
      ) &&
      filterFunction?.(userAsFilterFormat(user)),
    [choosenRoleIds, filterFunction],
  );

  const userListSorter = useCallback(
    ({ entity: a }, { entity: b }) => sortByText(fullName(a), fullName(b)),
    [],
  );
  const [filterDrawerVisible, setFilterDrawerVisibility] = useState(false);

  const actionBar =
    userCustomFields.length > 0 ? (
      <>
        <TaskFilterButton
          onClick={() => setFilterDrawerVisibility(!filterDrawerVisible)}
          loading={isUserFieldsLoading}
        >
          <FormattedMessage
            id="captions.buttons.filter"
            values={{
              count: validFilters.length,
            }}
          />
        </TaskFilterButton>
        {validFilters.length > 0 ? (
          <Button
            size="small"
            type="link"
            onClick={() => {
              setFilter(defaultFilter);
            }}
          >
            <FormattedMessage id="captions.buttons.clear_filter" />
          </Button>
        ) : null}

        <FilterDrawer
          title={projectLookup[projectId]?.projectName}
          visible={filterDrawerVisible}
          filter={filter}
          onSubmit={(next) => {
            setFilter(next);
            setFilterDrawerVisibility(false);
          }}
          onClose={() => setFilterDrawerVisibility(false)}
          fields={allUserFields}
        />
      </>
    ) : (
      <RoleSelectionDropdown
        roleOptions={getUserRoleOptions(roles, usersNotInProjects)}
        numberOfEntities={usersNotInProjects.length}
        choosenRoles={choosenRoles}
        onRoleSelectionChange={setChoosenRoles}
      />
    );

  const renderUser = useCallback(
    (user: User) => (
      <NameCellWithoutPadding
        primaryText={
          <TextWrapperWithEllipsis>{fullName(user)}</TextWrapperWithEllipsis>
        }
        secondaryText={user.role?.roleName}
        logo={
          <Avatar
            src={getThumbnailUrl(user.photoUrl)}
            name={fullName(user)}
            companyLogoUrl={user.company.companyLogoUrl}
          />
        }
      />
    ),
    [],
  );

  const columns: ColumnsType<AddToProjectRecordType<User>> = [
    {
      title: useTranslation('team_members'),
      dataIndex: 'entity',
      key: 'entity',
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: 'ascend',
      sorter: userListSorter,
      render: renderUser,
    },
    {
      title: '',
      dataIndex: 'company',
      key: 'company',
      width: 48,
      render: (_, { entity }) =>
        entity.userType === UserType.Partner && entity.company != null ? (
          <PartnerCompanyInfo companyName={entity.company.companyName} />
        ) : (
          <></>
        ),
    },
  ];

  return (
    <ModalContent
      entityIdKey="userId"
      actionBar={actionBar}
      entities={usersNotInProjects}
      searchPlaceholder={searchPlaceholder}
      filterFunction={combinedFilterFunction}
      selectedEntities={selectedUsers}
      updateSelectedEntities={updateSelectedUsers}
      columns={columns}
      isMultiSelect
    />
  );
};
